import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Search, Filter, MoreVertical, Eye, Pencil, Trash2,
  MapPin, Calendar, User, LayoutGrid, List, Award, TrendingUp,
  Receipt, Banknote, Building, ChevronUp, ChevronDown,
  ChevronsUpDown, Briefcase, Clock, ShieldCheck,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-PH", {
    style: "currency", currency: "PHP",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(value || 0);

const formatDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return d; }
};

const formatProjectDisplayId = (project) => {
  if (project.project_code?.trim()) return project.project_code;
  return project.project_id ? project.project_id.slice(-8) : "No ID";
};

// ── Status badge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    Ongoing: "bg-blue-500/15 text-blue-600 border-blue-500/30",
    Delayed: "bg-red-500/15 text-red-600 border-red-500/30",
    Completed: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  };
  return <Badge className={`text-xs font-medium ${map[status] || "bg-muted text-muted-foreground"}`}>{status}</Badge>;
};

// ── Sortable table header ─────────────────────────────────────────────────────
const SortableHead = ({ label, field, sortConfig, onSort, className = "" }) => {
  const active = sortConfig.field === field;
  return (
    <TableHead
      className={`cursor-pointer select-none hover:text-foreground transition-colors ${className}`}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {active ? (
          sortConfig.dir === "asc"
            ? <ChevronUp className="w-3 h-3 text-primary" />
            : <ChevronDown className="w-3 h-3 text-primary" />
        ) : (
          <ChevronsUpDown className="w-3 h-3 opacity-30" />
        )}
      </div>
    </TableHead>
  );
};

// ── Empty blank form ──────────────────────────────────────────────────────────
const BLANK_FORM = {
  title: "", abc: 0, contract_amount: 0, category: "", location: "",
  duration_days: 0, start_date: "", end_date: "", bid_date: "",
  notice_of_award: "", notice_to_proceed: "", implementing_office: "",
  assigned_project_engineer: "", assigned_resident_engineer: "",
  status: "Ongoing", license: "", project_code: "",
  retention_pct: 10, retention_amount: 0, vat_amount: 0,
  mobilization_fee: 0, advance_payment: 0, performance_bond: 0,
  defects_liability_period: "", cfa_issued_date: "",
};

const IMPLEMENTING_OFFICES = [
  "DPWH - SDN 2ND DEO",
  "DPWH - DIDEO",
  "CITY OF SURIGAO",
  "LGU - CAGDIANAO",
  "LGU - TUBAJON",
  "LGU - LIBJO",
  "LGU - LORETO",
  "NIA - SURIGAO",
  "NIA – DINAGAT",
  "PROVINCE OF SURIGAO DEL NORTE",
  "PROVINCE OF DINAGAT",
  "BARANGAY PANAMAON",
  "PPA SNSU",
  "CARAGA REGIONAL HOSPITAL",
  "DEPED – SURIGAO CITY",
  "DEPED – SDN",
  "DEPED - DINAGAT",
];

export default function Projects() {
  const [projects, setProjects]               = useState([]);
  const [projectProgress, setProjectProgress] = useState({});
  const [projectBilling, setProjectBilling]   = useState({});
  const [projectExpenses, setProjectExpenses] = useState({});
  const [loading, setLoading]                 = useState(true);

  // filters
  const [searchTerm, setSearchTerm]       = useState("");
  const [statusFilter, setStatusFilter]   = useState("Ongoing");
  const [yearFilter, setYearFilter]       = useState("all");
  const [licenseFilter, setLicenseFilter] = useState("all");
  const [ioFilter, setIoFilter]           = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [implementingOffices] = useState(IMPLEMENTING_OFFICES);

  // view & sort
  const [viewMode, setViewMode]     = useState("list");
  const [sortConfig, setSortConfig] = useState({ field: null, dir: "asc" });

  // dialog
  const [isDialogOpen, setIsDialogOpen]   = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData]           = useState(BLANK_FORM);
  const [activeFormTab, setActiveFormTab] = useState("general");
  const [mainTab, setMainTab] = useState("projects");
  const [cfaDialog, setCfaDialog] = useState(null); // { project_id, title, date }

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects`, { withCredentials: true });
      const data = response.data;
      setProjects(data);

      const progressMap = {}, billingMap = {}, expenseMap = {};
      await Promise.all(data.map(async (p) => {
        try {
          const [progRes, billRes, expRes] = await Promise.all([
            axios.get(`${API}/projects/${p.project_id}/progress`, { withCredentials: true }),
            axios.get(`${API}/projects/${p.project_id}/billing`, { withCredentials: true }),
            axios.get(`${API}/expenses/by-project/${p.project_id}`, { withCredentials: true }).catch(() => ({ data: [] })),
          ]);
          const totalProg = (progRes.data || []).reduce((s, l) => s + (l.physical_percent || 0), 0);
          progressMap[p.project_id] = Math.min(totalProg, 100);

          const totalBilled = (billRes.data || []).filter(b => b.status === "Paid").reduce((s, b) => s + (b.gross_amount || 0), 0);
          billingMap[p.project_id] = p.contract_amount > 0 ? (totalBilled / p.contract_amount) * 100 : 0;

          const totalExp = (expRes.data || []).reduce((s, e) => s + (e.amount || 0), 0);
          expenseMap[p.project_id] = p.contract_amount > 0 ? (totalExp / p.contract_amount) * 100 : 0;
        } catch {
          progressMap[p.project_id] = 0;
          billingMap[p.project_id]  = 0;
          expenseMap[p.project_id]  = 0;
        }
      }));

      setProjectProgress(progressMap);
      setProjectBilling(billingMap);
      setProjectExpenses(expenseMap);
    } catch {
      toast.error("Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // ── Auto-calc financial fields when contract changes ───────────────────────
  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === "contract_amount" || field === "retention_pct") {
        const ca  = field === "contract_amount" ? parseFloat(value) || 0 : prev.contract_amount;
        const pct = field === "retention_pct"   ? parseFloat(value) || 0 : prev.retention_pct;
        next.retention_amount = parseFloat(((ca * pct) / 100).toFixed(2));
        next.vat_amount       = parseFloat((ca * 0.12).toFixed(2));
      }
      return next;
    });
  };

  // ── CRUD ───────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        abc: parseFloat(formData.abc) || 0,
        contract_amount: parseFloat(formData.contract_amount) || 0,
        duration_days: parseInt(formData.duration_days) || 0,
        retention_pct: parseFloat(formData.retention_pct) || 0,
        retention_amount: parseFloat(formData.retention_amount) || 0,
        vat_amount: parseFloat(formData.vat_amount) || 0,
        mobilization_fee: parseFloat(formData.mobilization_fee) || 0,
        advance_payment: parseFloat(formData.advance_payment) || 0,
        performance_bond: parseFloat(formData.performance_bond) || 0,
      };
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.project_id}`, payload, { withCredentials: true });
        toast.success("Project updated");
      } else {
        await axios.post(`${API}/projects`, payload, { withCredentials: true });
        toast.success("Project created");
      }
      setIsDialogOpen(false);
      setEditingProject(null);
      setFormData(BLANK_FORM);
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (projectId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}`, { withCredentials: true });
      toast.success("Project deleted");
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const handleSaveCfaDate = async () => {
    if (!cfaDialog) return;
    const project = projects.find(p => p.project_id === cfaDialog.project_id);
    if (!project) return;
    if (!cfaDialog.date) {
      toast.error("Select the CFA issued date");
      return;
    }
    try {
      await axios.put(
        `${API}/projects/${cfaDialog.project_id}`,
        { cfa_issued_date: cfaDialog.date },
        { withCredentials: true }
      );
      toast.success("CFA issued date saved");
      setCfaDialog(null);
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save CFA date");
    }
  };

  const openEditDialog = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title || "",
      abc: project.abc || 0,
      contract_amount: project.contract_amount || 0,
      category: project.category || "",
      location: project.location || "",
      duration_days: project.duration_days || 0,
      start_date: project.start_date || "",
      end_date: project.end_date || "",
      bid_date: project.bid_date || "",
      notice_of_award: project.notice_of_award || "",
      notice_to_proceed: project.notice_to_proceed || "",
      implementing_office: project.implementing_office || "",
      assigned_project_engineer: project.assigned_project_engineer || "",
      assigned_resident_engineer: project.assigned_resident_engineer || "",
      status: project.status || "Ongoing",
      license: project.license || "",
      project_code: project.project_code || "",
      retention_pct: project.retention_pct ?? 10,
      retention_amount: project.retention_amount || 0,
      vat_amount: project.vat_amount || 0,
      mobilization_fee: project.mobilization_fee || 0,
      advance_payment: project.advance_payment || 0,
      performance_bond: project.performance_bond || 0,
      defects_liability_period: project.defects_liability_period || "",
      cfa_issued_date: project.cfa_issued_date || "",
    });
    setActiveFormTab("general");
    setIsDialogOpen(true);
  };

  // ── Derived filter options ─────────────────────────────────────────────────
  const availableLicenses  = [...new Set(projects.map(p => p.license).filter(Boolean))].sort();
  const availableYears     = [...new Set(projects.map(p => {
    const d = p.notice_of_award || p.bid_date || p.start_date;
    return d ? new Date(d).getFullYear() : null;
  }).filter(Boolean))].sort((a, b) => b - a);
  const availableCategories = ["Roads","Building","Flood Control","Bridge","Slope Protection","Canal/Dam","Water Supply","Ports and Harbor","Supply","Streetlights"];

  // ── Smart search + filter ──────────────────────────────────────────────────
  const filtered = projects
    .filter(p => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (licenseFilter !== "all" && p.license !== licenseFilter) return false;
      if (ioFilter !== "all" && p.implementing_office !== ioFilter) return false;
      if (categoryFilter !== "all" && p.category !== categoryFilter) return false;
      if (yearFilter !== "all") {
        const d = p.notice_of_award || p.bid_date || p.start_date;
        if (!d || new Date(d).getFullYear().toString() !== yearFilter) return false;
      }
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        return Object.values(p).some(v => String(v).toLowerCase().includes(q));
      }
      return true;
    });

  // ── Sort (list view) ───────────────────────────────────────────────────────
  const handleSort = (field) => {
    setSortConfig(prev =>
      prev.field === field
        ? { field, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { field, dir: "asc" }
    );
  };

  const sortedFiltered = [...filtered].sort((a, b) => {
    if (!sortConfig.field) return 0;
    const f = sortConfig.field;
    let va = a[f] ?? "", vb = b[f] ?? "";

    // numeric fields
    if (["contract_amount", "abc"].includes(f)) { va = parseFloat(va) || 0; vb = parseFloat(vb) || 0; }
    else if (f === "physical_pct")   { va = projectProgress[a.project_id] || 0; vb = projectProgress[b.project_id] || 0; }
    else if (f === "billing_pct")    { va = projectBilling[a.project_id]  || 0; vb = projectBilling[b.project_id]  || 0; }
    else if (f === "expense_pct")    { va = projectExpenses[a.project_id] || 0; vb = projectExpenses[b.project_id] || 0; }
    else { va = String(va).toLowerCase(); vb = String(vb).toLowerCase(); }

    if (va < vb) return sortConfig.dir === "asc" ? -1 : 1;
    if (va > vb) return sortConfig.dir === "asc" ? 1 : -1;
    return 0;
  });

  // ── Totals for list footer ─────────────────────────────────────────────────
  const totalContract = filtered.reduce((s, p) => s + (p.contract_amount || 0), 0);

  // ── CFA Reminders ──────────────────────────────────────────────────────────
  // CFA is due 1 year after whichever is later: end_date or revised_completion_date
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cfaProjects = projects
    .map(p => {
      const dates = [p.end_date, p.revised_completion_date].filter(Boolean).map(d => new Date(d));
      if (dates.length === 0) return null;
      const latestCompletion = new Date(Math.max(...dates));
      const cfaDue = new Date(latestCompletion);
      cfaDue.setFullYear(cfaDue.getFullYear() + 1);
      const cfaDaysLeft = Math.ceil((cfaDue - today) / (1000 * 60 * 60 * 24));
      return { ...p, latestCompletion, cfaDue, cfaDaysLeft };
    })
    .filter(Boolean)
    .sort((a, b) => (!!a.cfa_issued_date - !!b.cfa_issued_date) || (a.cfaDaysLeft - b.cfaDaysLeft));

  // ── SLCC (Single Largest Contract per License × Category) ─────────────────
  const CATEGORIES = ["Roads","Building","Flood Control","Bridge","Slope Protection","Canal/Dam","Water Supply","Ports and Harbor","Supply","Streetlights"];
  const completedProjects = projects.filter(p => p.status === "Completed");
  const allLicenses = [...new Set(completedProjects.map(p => p.license).filter(Boolean))].sort();

  // slccMap[license][category] = { contract_amount, title, project_id }
  const slccMap = {};
  for (const p of completedProjects) {
    if (!p.license || !p.category) continue;
    if (!slccMap[p.license]) slccMap[p.license] = {};
    const cur = slccMap[p.license][p.category];
    if (!cur || (p.contract_amount || 0) > cur.contract_amount) {
      slccMap[p.license][p.category] = {
        contract_amount: p.contract_amount || 0,
        title: p.title,
        project_id: p.project_id,
        project_code: p.project_code?.trim() || p.project_id?.slice(-8) || "—",
      };
    }
  }

  if (loading)
    return (
      <div className="space-y-5 animate-fade-in">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-7 w-32 bg-muted rounded animate-pulse" />
            <div className="h-4 w-56 bg-muted rounded animate-pulse" />
          </div>
          <div className="h-9 w-32 bg-muted rounded animate-pulse" />
        </div>
        {/* Filter bar skeleton */}
        <div className="flex gap-2 flex-wrap">
          {[200, 150, 150, 150, 130, 130].map((w, i) => (
            <div key={i} className="h-9 bg-muted rounded animate-pulse" style={{ width: w }} />
          ))}
        </div>
        {/* Table skeleton */}
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="h-10 bg-muted/60 animate-pulse border-b border-border" />
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className={`flex items-center gap-4 px-4 py-3 border-b border-border ${i % 2 !== 0 ? "bg-muted/20" : ""}`}>
              <div className="h-4 w-20 bg-muted rounded animate-pulse shrink-0" />
              <div className="h-4 flex-1 bg-muted rounded animate-pulse" />
              <div className="h-4 w-24 bg-muted rounded animate-pulse shrink-0" />
              <div className="h-4 w-20 bg-muted rounded animate-pulse shrink-0" />
              <div className="h-4 w-28 bg-muted rounded animate-pulse shrink-0" />
              <div className="h-5 w-16 bg-muted rounded-full animate-pulse shrink-0" />
              <div className="h-4 w-14 bg-muted rounded animate-pulse shrink-0" />
              <div className="h-4 w-14 bg-muted rounded animate-pulse shrink-0" />
              <div className="h-4 w-14 bg-muted rounded animate-pulse shrink-0" />
              <div className="h-4 w-20 bg-muted rounded animate-pulse shrink-0" />
              <div className="h-6 w-16 bg-muted rounded animate-pulse shrink-0" />
            </div>
          ))}
        </div>
      </div>
    );

  return (
    <div data-testid="projects-page" className="space-y-5 animate-fade-in">

      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Projects</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {filtered.length} of {projects.length} project{projects.length !== 1 ? "s" : ""}
            {" · "}{formatCurrency(totalContract)} total contract
          </p>
        </div>
        <Button
          data-testid="create-project-btn"
          className="btn-primary flex items-center gap-2"
          onClick={() => { setEditingProject(null); setFormData(BLANK_FORM); setActiveFormTab("general"); setIsDialogOpen(true); }}
        >
          <Plus className="w-4 h-4" /> New Project
        </Button>
      </div>

      {/* ── Main Tabs ── */}
      <div className="flex gap-0 border-b border-border">
        {[
          { id: "projects", label: "All Projects" },
          { id: "cfa",      label: "CFA Reminders" },
          { id: "slcc",     label: "SLCC" },
        ].map(t => {
          const urgentCount = t.id === "cfa" ? cfaProjects.filter(p => !p.cfa_issued_date && p.cfaDaysLeft <= 30).length : 0;
          return (
            <button key={t.id} onClick={() => setMainTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                mainTab === t.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}>
              {t.label}
              {urgentCount > 0 && (
                <span className="text-[10px] bg-red-500 text-white rounded-full px-1.5 py-0.5 font-bold leading-none">
                  {urgentCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Filters ── */}
      {mainTab === "projects" && (<>
      <div className="flex flex-wrap gap-2">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="search-projects"
            placeholder="Smart search…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-9 input-streamcon"
          />
        </div>

        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[150px] input-streamcon">
            <Briefcase className="w-4 h-4 mr-1.5 text-muted-foreground" />
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {availableCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={ioFilter} onValueChange={setIoFilter}>
          <SelectTrigger data-testid="io-filter" className="w-[150px] input-streamcon">
            <Building className="w-4 h-4 mr-1.5 text-muted-foreground" />
            <SelectValue placeholder="I.O." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All I.O.</SelectItem>
            {implementingOffices.map(io => <SelectItem key={io} value={io}>{io}</SelectItem>)}
            {/* Show any existing project I.O. values not in the fixed list */}
            {[...new Set(projects.map(p => p.implementing_office).filter(Boolean))]
              .filter(io => !IMPLEMENTING_OFFICES.includes(io))
              .map(io => <SelectItem key={io} value={io}>{io} <span className="text-muted-foreground text-[10px]">(legacy)</span></SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={licenseFilter} onValueChange={setLicenseFilter}>
          <SelectTrigger data-testid="license-filter" className="w-[150px] input-streamcon">
            <Award className="w-4 h-4 mr-1.5 text-muted-foreground" />
            <SelectValue placeholder="License" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Licenses</SelectItem>
            {availableLicenses.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger data-testid="year-filter" className="w-[130px] input-streamcon">
            <Calendar className="w-4 h-4 mr-1.5 text-muted-foreground" />
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            {availableYears.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="status-filter" className="w-[130px] input-streamcon">
            <Filter className="w-4 h-4 mr-1.5 text-muted-foreground" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="Ongoing">Ongoing</SelectItem>
            <SelectItem value="Delayed">Delayed</SelectItem>
            <SelectItem value="Completed">Completed</SelectItem>
          </SelectContent>
        </Select>

        {/* View toggle */}
        <div className="flex border border-border rounded-sm overflow-hidden">
          <Button data-testid="view-grid-btn" variant="ghost" size="sm"
            className={`rounded-none px-3 ${viewMode === "grid" ? "bg-muted" : ""}`}
            onClick={() => setViewMode("grid")}>
            <LayoutGrid className="w-4 h-4" />
          </Button>
          <Button data-testid="view-list-btn" variant="ghost" size="sm"
            className={`rounded-none px-3 border-l border-border ${viewMode === "list" ? "bg-muted" : ""}`}
            onClick={() => setViewMode("list")}>
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* ── Grid View ── */}
      {viewMode === "grid" && (
        <>
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No projects found</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(project => {
                const phys = projectProgress[project.project_id] || 0;
                const bill = projectBilling[project.project_id]  || 0;
                const exp  = projectExpenses[project.project_id] || 0;
                return (
                  <Card key={project.project_id} data-testid={`project-card-${project.project_id}`}
                    className="card-streamcon hover:border-primary/30 transition-colors flex flex-col">

                    {/* Card header */}
                    <CardHeader className="pb-2 pt-4 px-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                              {formatProjectDisplayId(project)}
                            </span>
                            {project.license && (
                              <span className="text-[10px] font-mono text-primary/80 bg-primary/8 px-1.5 py-0.5 rounded">
                                {project.license}
                              </span>
                            )}
                          </div>
                          <CardTitle className="text-sm font-semibold text-foreground leading-snug line-clamp-2">
                            {project.title}
                          </CardTitle>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <StatusBadge status={project.status} />
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground">
                                <MoreVertical className="w-3.5 h-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to={`/projects/${project.project_id}`}><Eye className="w-4 h-4 mr-2" />View Details</Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEditDialog(project)}>
                                <Pencil className="w-4 h-4 mr-2" />Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem className="text-red-500" onClick={() => handleDelete(project.project_id)}>
                                <Trash2 className="w-4 h-4 mr-2" />Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="px-4 pb-4 flex-1 flex flex-col gap-3">

                      {/* Info rows */}
                      <div className="space-y-1.5">
                        {project.location && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <MapPin className="w-3 h-3 shrink-0" />
                            <span className="truncate">{project.location}</span>
                          </div>
                        )}
                        {project.implementing_office && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Building className="w-3 h-3 shrink-0" />
                            <span className="truncate">{project.implementing_office}</span>
                          </div>
                        )}
                        {project.assigned_project_engineer && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <User className="w-3 h-3 shrink-0" />
                            <span className="truncate">{project.assigned_project_engineer}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3 shrink-0" />
                          <span>{formatDate(project.start_date)} → {formatDate(project.end_date)}</span>
                        </div>
                        {project.category && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Briefcase className="w-3 h-3 shrink-0" />
                            <span>{project.category}</span>
                            {project.duration_days > 0 && (
                              <span className="ml-auto">{project.duration_days} days</span>
                            )}
                          </div>
                        )}
                      </div>

                      <Separator />

                      {/* Progress bars */}
                      <div className="space-y-2">
                        {[
                          { label: "Physical", value: phys, icon: TrendingUp, color: "bg-primary" },
                          { label: "Billing",  value: bill, icon: Receipt,    color: "bg-blue-500" },
                          { label: "Expense",  value: exp,  icon: Banknote,   color: exp > 100 ? "bg-red-500" : "bg-yellow-400" },
                        ].map(({ label, value, icon: Icon, color }) => (
                          <div key={label}>
                            <div className="flex justify-between text-[10px] mb-0.5">
                              <span className="text-muted-foreground flex items-center gap-1">
                                <Icon className="w-2.5 h-2.5" />{label}
                              </span>
                              <span className={`font-mono font-medium ${value > 100 && label === "Expense" ? "text-red-500" : "text-foreground"}`}>
                                {value.toFixed(2)}%
                              </span>
                            </div>
                            <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(value, 100)}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>

                      <Separator />

                      {/* Contract amount */}
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Contract Amount</span>
                        <span className="text-sm font-mono font-bold">{formatCurrency(project.contract_amount)}</span>
                      </div>

                      {/* Financial pills (only if set) */}
                      {(project.retention_amount > 0 || project.vat_amount > 0 || project.advance_payment > 0) && (
                        <div className="flex flex-wrap gap-1">
                          {project.retention_amount > 0 && (
                            <span className="text-[10px] bg-muted px-2 py-0.5 rounded-full text-muted-foreground">
                              Ret: {formatCurrency(project.retention_amount)}
                            </span>
                          )}
                          {project.vat_amount > 0 && (
                            <span className="text-[10px] bg-muted px-2 py-0.5 rounded-full text-muted-foreground">
                              VAT: {formatCurrency(project.vat_amount)}
                            </span>
                          )}
                          {project.advance_payment > 0 && (
                            <span className="text-[10px] bg-muted px-2 py-0.5 rounded-full text-muted-foreground">
                              Adv: {formatCurrency(project.advance_payment)}
                            </span>
                          )}
                        </div>
                      )}

                      {/* View button */}
                      <Link to={`/projects/${project.project_id}`} className="mt-auto">
                        <Button variant="outline" className="w-full text-xs h-8 border-border hover:bg-muted/50">
                          <Eye className="w-3 h-3 mr-1.5" /> View Project
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── List View ── */}
      {viewMode === "list" && (
        <Card className="card-streamcon">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header border-b border-border">
                    <SortableHead label="Project ID"  field="project_code"     sortConfig={sortConfig} onSort={handleSort} />
                    <SortableHead label="Title"       field="title"            sortConfig={sortConfig} onSort={handleSort} />
                    <SortableHead label="Category"    field="category"         sortConfig={sortConfig} onSort={handleSort} />
                    <SortableHead label="License"     field="license"          sortConfig={sortConfig} onSort={handleSort} />
                    <SortableHead label="Contract"    field="contract_amount"  sortConfig={sortConfig} onSort={handleSort} className="text-right" />
                    <SortableHead label="Status"      field="status"           sortConfig={sortConfig} onSort={handleSort} />
                    <SortableHead label="Physical %"  field="physical_pct"     sortConfig={sortConfig} onSort={handleSort} className="text-center" />
                    <SortableHead label="Billing %"   field="billing_pct"      sortConfig={sortConfig} onSort={handleSort} className="text-center" />
                    <SortableHead label="Expense %"   field="expense_pct"      sortConfig={sortConfig} onSort={handleSort} className="text-center" />
                    <SortableHead label="End Date"    field="end_date"         sortConfig={sortConfig} onSort={handleSort} />
                    <TableHead className="text-right text-muted-foreground">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedFiltered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-10 text-muted-foreground">No projects found</TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {sortedFiltered.map((project, idx) => {
                        const phys = projectProgress[project.project_id] || 0;
                        const bill = projectBilling[project.project_id]  || 0;
                        const exp  = projectExpenses[project.project_id] || 0;
                        const rowBg = idx % 2 === 0 ? "" : "bg-muted/30";
                        return (
                          <TableRow key={project.project_id} className={`table-row ${rowBg}`}>
                            <TableCell className="font-mono text-xs">{formatProjectDisplayId(project)}</TableCell>
                            {/* ── CHANGED: removed max-w-[220px] so the cell expands to fit the full title ── */}
                            <TableCell>
                              <Link to={`/projects/${project.project_id}`}
                                className="text-foreground hover:text-primary transition-colors font-medium text-sm">
                                {project.title}
                              </Link>
                              {project.implementing_office && (
                                <p className="text-[10px] text-muted-foreground truncate">{project.implementing_office}</p>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">{project.category || "—"}</TableCell>
                            <TableCell className="text-xs font-mono text-muted-foreground">{project.license || "—"}</TableCell>
                            <TableCell className="text-right font-mono font-bold text-sm">{formatCurrency(project.contract_amount)}</TableCell>
                            <TableCell><StatusBadge status={project.status} /></TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-0.5">
                                <span className="text-xs font-mono text-primary">{phys.toFixed(2)}%</span>
                                <div className="h-1 w-14 bg-muted rounded-full overflow-hidden">
                                  <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(phys, 100)}%` }} />
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-0.5">
                                <span className="text-xs font-mono text-blue-500">{bill.toFixed(2)}%</span>
                                <div className="h-1 w-14 bg-muted rounded-full overflow-hidden">
                                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(bill, 100)}%` }} />
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-0.5">
                                <span className={`text-xs font-mono ${exp > 100 ? "text-red-500" : "text-yellow-400"}`}>{exp.toFixed(2)}%</span>
                                <div className="h-1 w-14 bg-muted rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${exp > 100 ? "bg-red-500" : "bg-yellow-400"}`} style={{ width: `${Math.min(exp, 100)}%` }} />
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">{formatDate(project.end_date)}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-0.5">
                                <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                                  <Link to={`/projects/${project.project_id}`}><Eye className="w-3.5 h-3.5" /></Link>
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditDialog(project)}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-400"
                                  onClick={() => handleDelete(project.project_id)}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Totals row */}
                      <TableRow className="border-t-2 border-border bg-muted/30 font-semibold">
                        <TableCell colSpan={4} className="text-xs text-muted-foreground py-2">
                          {filtered.length} project{filtered.length !== 1 ? "s" : ""} shown
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold text-sm">{formatCurrency(totalContract)}</TableCell>
                        <TableCell colSpan={6} />
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      </>)}

      {/* ── CFA Reminders Tab ── */}
      {mainTab === "cfa" && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Certificate of Final Acceptance is due <span className="font-semibold text-foreground">1 year</span> after
            the latest of End Date or Revised Completion Date. Pending CFAs are listed first by urgency; issued ones move to the bottom.
          </p>
          {cfaProjects.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <ShieldCheck className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No projects with completion dates yet</p>
            </div>
          ) : (
            <Card className="card-streamcon">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header border-b border-border">
                        <TableHead>Project ID</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>End Date</TableHead>
                        <TableHead>Revised Completion</TableHead>
                        <TableHead>CFA Due Date</TableHead>
                        <TableHead className="text-center">Days Left</TableHead>
                        <TableHead className="text-center">CFA Status</TableHead>
                        <TableHead className="text-center">CFA Issued</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cfaProjects.map(project => {
                        const isDone     = !!project.cfa_issued_date;
                        const isOverdue  = !isDone && project.cfaDaysLeft < 0;
                        const isUrgent   = !isDone && project.cfaDaysLeft >= 0 && project.cfaDaysLeft <= 30;
                        const isWarning  = !isDone && project.cfaDaysLeft > 30 && project.cfaDaysLeft <= 90;
                        const rowBg = isDone ? "bg-emerald-500/5" : isOverdue ? "bg-red-500/5" : isUrgent ? "bg-orange-500/5" : isWarning ? "bg-yellow-500/5" : "";
                        return (
                          <TableRow key={project.project_id} className={`table-row ${rowBg}`}>
                            <TableCell className="font-mono text-xs">{formatProjectDisplayId(project)}</TableCell>
                            <TableCell>
                              <Link to={`/projects/${project.project_id}`}
                                className="text-foreground hover:text-primary transition-colors font-medium text-sm">
                                {project.title}
                              </Link>
                              {project.implementing_office && (
                                <p className="text-[10px] text-muted-foreground">{project.implementing_office}</p>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">{project.category || "—"}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{formatDate(project.end_date)}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{formatDate(project.revised_completion_date)}</TableCell>
                            <TableCell className="text-xs font-semibold">{formatDate(project.cfaDue)}</TableCell>
                            <TableCell className="text-center">
                              {isDone ? (
                                <span className="text-xs text-emerald-600 font-medium">—</span>
                              ) : (
                                <span className={`text-sm font-bold font-mono ${
                                  isOverdue ? "text-red-500" : isUrgent ? "text-orange-500" : isWarning ? "text-yellow-500" : "text-emerald-500"
                                }`}>
                                  {isOverdue ? `${Math.abs(project.cfaDaysLeft)}d overdue` : `${project.cfaDaysLeft}d`}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {isDone     && <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 text-xs">Done</Badge>}
                              {isOverdue  && <Badge className="bg-red-500/15 text-red-600 border-red-500/30 text-xs">Overdue</Badge>}
                              {isUrgent   && <Badge className="bg-orange-500/15 text-orange-600 border-orange-500/30 text-xs">Due Soon</Badge>}
                              {isWarning  && <Badge className="bg-yellow-500/15 text-yellow-600 border-yellow-500/30 text-xs">Upcoming</Badge>}
                              {!isDone && !isOverdue && !isUrgent && !isWarning && <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 text-xs">On Track</Badge>}
                            </TableCell>
                            <TableCell className="text-center">
                              {isDone ? (
                                <div>
                                  <p className="text-xs font-semibold text-emerald-600">{formatDate(project.cfa_issued_date)}</p>
                                  <button onClick={() => setCfaDialog({ project_id: project.project_id, title: project.title, date: project.cfa_issued_date })}
                                    className="text-[10px] text-muted-foreground hover:text-primary underline">
                                    Edit
                                  </button>
                                </div>
                              ) : (
                                <Button size="sm" variant="outline"
                                  className="h-7 text-xs gap-1 border-emerald-500/40 text-emerald-600 hover:bg-emerald-500/10"
                                  onClick={() => setCfaDialog({ project_id: project.project_id, title: project.title, date: "" })}>
                                  <ShieldCheck className="w-3 h-3" /> Issue CFA
                                </Button>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-0.5">
                                <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                                  <Link to={`/projects/${project.project_id}`}><Eye className="w-3.5 h-3.5" /></Link>
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditDialog(project)}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ── SLCC Tab ── */}
      {mainTab === "slcc" && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Showing the <span className="font-semibold text-foreground">Single Largest Contract Amount</span> per License × Category — <span className="font-semibold text-foreground">Completed</span> projects only.
          </p>
          {allLicenses.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Award className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No completed projects with licenses yet</p>
            </div>
          ) : (
            <Card className="card-streamcon">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr>
                        <th className="sticky left-0 z-10 bg-[#1B3A6B] text-white px-4 py-2.5 text-left font-bold border border-border min-w-[120px]">
                          License
                        </th>
                        {CATEGORIES.map(cat => (
                          <th key={cat} className="bg-[#1B3A6B] text-white px-3 py-2.5 text-center font-semibold border border-border min-w-[140px]">
                            {cat}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allLicenses.map((license, licIdx) => (
                        <tr key={license} className={licIdx % 2 === 0 ? "bg-card" : "bg-muted/30"}>
                          <td className={`sticky left-0 z-10 px-4 py-2.5 font-bold text-primary border border-border ${licIdx % 2 === 0 ? "bg-card" : "bg-muted/30"}`}>
                            {license}
                          </td>
                          {CATEGORIES.map(cat => {
                            const entry = slccMap[license]?.[cat];
                            return (
                              <td key={cat} className="border border-border px-3 py-2 text-center align-top">
                                {entry ? (
                                  <div className="space-y-0.5">
                                    <p className="font-mono font-bold text-foreground">{formatCurrency(entry.contract_amount)}</p>
                                    <span className="inline-block font-mono text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                      {entry.project_code}
                                    </span>
                                    <Link to={`/projects/${entry.project_id}`}
                                      className="text-[10px] text-muted-foreground hover:text-primary transition-colors line-clamp-2 leading-tight block">
                                      {entry.title}
                                    </Link>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground/40">—</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ── Create / Edit Dialog ── */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProject ? "Edit Project" : "New Project"}</DialogTitle>
          </DialogHeader>

          {/* Tab pills */}
          <div className="flex gap-1 bg-muted rounded-md p-1 w-fit">
            {[
              { id: "general",   label: "General" },
              { id: "dates",     label: "Dates & People" },
              { id: "financial", label: "Financials" },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveFormTab(tab.id)}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  activeFormTab === tab.id
                    ? "bg-background shadow text-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="space-y-4 pt-1">

            {/* ─ Tab: General ─ */}
            {activeFormTab === "general" && (
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2 space-y-1.5">
                  <Label>Project Title *</Label>
                  <Input data-testid="project-title-input" value={formData.title}
                    onChange={e => handleFormChange("title", e.target.value)}
                    placeholder="Full project title" className="input-streamcon" />
                </div>
                <div className="space-y-1.5">
                  <Label>Project ID / Code</Label>
                  <Input data-testid="project-code-input" value={formData.project_code}
                    onChange={e => handleFormChange("project_code", e.target.value)}
                    placeholder="e.g. 25NG0073" className="input-streamcon" />
                </div>
                <div className="space-y-1.5">
                  <Label>License</Label>
                  <Input data-testid="project-license-input" value={formData.license}
                    onChange={e => handleFormChange("license", e.target.value)}
                    placeholder="e.g. LIC-2025-001" className="input-streamcon" />
                </div>
                <div className="space-y-1.5">
                  <Label>Category</Label>
                  <Select value={formData.category} onValueChange={v => handleFormChange("category", v)}>
                    <SelectTrigger className="input-streamcon"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      {["Roads","Building","Flood Control","Bridge","Slope Protection","Canal/Dam","Water Supply","Ports and Harbor","Supply","Streetlights"]
                        .map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={v => handleFormChange("status", v)}>
                    <SelectTrigger className="input-streamcon"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Ongoing">Ongoing</SelectItem>
                      <SelectItem value="Delayed">Delayed</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2 space-y-1.5">
                  <Label>Location</Label>
                  <Input value={formData.location} onChange={e => handleFormChange("location", e.target.value)}
                    placeholder="Project site address" className="input-streamcon" />
                </div>
                <div className="space-y-1.5">
                  <Label>Implementing Office</Label>
                  <Select value={formData.implementing_office} onValueChange={v => handleFormChange("implementing_office", v)}>
                    <SelectTrigger className="input-streamcon"><SelectValue placeholder="Select office" /></SelectTrigger>
                    <SelectContent>
                      {/* Show existing value as option if it's not in the fixed list */}
                      {formData.implementing_office && !IMPLEMENTING_OFFICES.includes(formData.implementing_office) && (
                        <SelectItem value={formData.implementing_office}>
                          {formData.implementing_office} <span className="text-muted-foreground text-[10px]">(existing)</span>
                        </SelectItem>
                      )}
                      {IMPLEMENTING_OFFICES.map(io => <SelectItem key={io} value={io}>{io}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Duration (Days)</Label>
                  <Input type="number" value={formData.duration_days}
                    onChange={e => handleFormChange("duration_days", e.target.value)} className="input-streamcon" />
                </div>
              </div>
            )}

            {/* ─ Tab: Dates & People ─ */}
            {activeFormTab === "dates" && (
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Bid Date</Label>
                  <Input type="date" value={formData.bid_date}
                    onChange={e => handleFormChange("bid_date", e.target.value)} className="input-streamcon" />
                </div>
                <div className="space-y-1.5">
                  <Label>Notice of Award (NOA)</Label>
                  <Input type="date" value={formData.notice_of_award}
                    onChange={e => handleFormChange("notice_of_award", e.target.value)} className="input-streamcon" />
                </div>
                <div className="space-y-1.5">
                  <Label>Notice to Proceed (NTP)</Label>
                  <Input type="date" value={formData.notice_to_proceed || ""}
                    onChange={e => handleFormChange("notice_to_proceed", e.target.value)} className="input-streamcon" />
                </div>
                <div className="space-y-1.5">
                  <Label>Start Date</Label>
                  <Input type="date" value={formData.start_date}
                    onChange={e => handleFormChange("start_date", e.target.value)} className="input-streamcon" />
                </div>
                <div className="space-y-1.5">
                  <Label>End Date</Label>
                  <Input type="date" value={formData.end_date}
                    onChange={e => handleFormChange("end_date", e.target.value)} className="input-streamcon" />
                </div>
                <div className="space-y-1.5">
                  <Label>Revised Completion Date</Label>
                  <Input type="date" value={formData.revised_completion_date || ""}
                    onChange={e => handleFormChange("revised_completion_date", e.target.value)} className="input-streamcon" />
                </div>
                <div className="space-y-1.5">
                  <Label>Project Engineer</Label>
                  <Input value={formData.assigned_project_engineer}
                    onChange={e => handleFormChange("assigned_project_engineer", e.target.value)} className="input-streamcon" />
                </div>
                <div className="space-y-1.5">
                  <Label>Resident Engineer</Label>
                  <Input value={formData.assigned_resident_engineer}
                    onChange={e => handleFormChange("assigned_resident_engineer", e.target.value)} className="input-streamcon" />
                </div>
              </div>
            )}

            {/* ─ Tab: Financials ─ */}
            {activeFormTab === "financial" && (
              <div className="space-y-4">
                {/* Base amounts */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Base Amounts</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>ABC (Approved Budget)</Label>
                      <Input type="number" value={formData.abc}
                        onChange={e => handleFormChange("abc", e.target.value)} className="input-streamcon" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Contract Amount</Label>
                      <Input type="number" value={formData.contract_amount}
                        onChange={e => handleFormChange("contract_amount", e.target.value)} className="input-streamcon" />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Auto-calculated */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                    Deductions & Guarantees
                    <span className="ml-2 text-[10px] font-normal normal-case text-muted-foreground/60">
                      Retention and VAT auto-calculate from Contract Amount
                    </span>
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>Retention %</Label>
                      <Input type="number" value={formData.retention_pct}
                        onChange={e => handleFormChange("retention_pct", e.target.value)}
                        placeholder="Default 10%" className="input-streamcon" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Retention Amount <span className="text-[10px] text-primary">(auto-calc)</span></Label>
                      <Input type="number" value={formData.retention_amount}
                        onChange={e => handleFormChange("retention_amount", e.target.value)}
                        className="input-streamcon bg-muted/30" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>VAT Amount (12%) <span className="text-[10px] text-primary">(auto-calc)</span></Label>
                      <Input type="number" value={formData.vat_amount}
                        onChange={e => handleFormChange("vat_amount", e.target.value)}
                        className="input-streamcon bg-muted/30" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Mobilization Fee</Label>
                      <Input type="number" value={formData.mobilization_fee}
                        onChange={e => handleFormChange("mobilization_fee", e.target.value)} className="input-streamcon" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Advance Payment</Label>
                      <Input type="number" value={formData.advance_payment}
                        onChange={e => handleFormChange("advance_payment", e.target.value)} className="input-streamcon" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Performance Bond</Label>
                      <Input type="number" value={formData.performance_bond}
                        onChange={e => handleFormChange("performance_bond", e.target.value)} className="input-streamcon" />
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-1.5">
                  <Label>Defects Liability Period</Label>
                  <Input value={formData.defects_liability_period}
                    onChange={e => handleFormChange("defects_liability_period", e.target.value)}
                    placeholder="e.g. 365 days after completion" className="input-streamcon" />
                </div>

                {/* Summary preview */}
                {formData.contract_amount > 0 && (
                  <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Financial Summary</p>
                    {[
                      { label: "Contract Amount", value: formData.contract_amount },
                      { label: `Retention (${formData.retention_pct}%)`, value: -formData.retention_amount, neg: true },
                      { label: "VAT (12%)", value: formData.vat_amount },
                      { label: "Mobilization Fee", value: formData.mobilization_fee },
                      { label: "Advance Payment", value: formData.advance_payment },
                      { label: "Performance Bond", value: formData.performance_bond },
                    ].filter(r => r.value !== 0).map(row => (
                      <div key={row.label} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{row.label}</span>
                        <span className={`font-mono ${row.neg ? "text-red-500" : "text-foreground"}`}>
                          {row.neg ? `(${formatCurrency(Math.abs(row.value))})` : formatCurrency(row.value)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="btn-primary" data-testid="submit-project-btn">
              {editingProject ? "Update Project" : "Create Project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ── CFA Issued Date Dialog ── */}
      <Dialog open={!!cfaDialog} onOpenChange={v => !v && setCfaDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" /> Issue Certificate of Final Acceptance
            </DialogTitle>
          </DialogHeader>
          {cfaDialog && (
            <div className="space-y-4 pt-1">
              <p className="text-sm text-muted-foreground leading-snug">{cfaDialog.title}</p>
              <div className="space-y-1.5">
                <Label>CFA Issued Date</Label>
                <Input type="date" value={cfaDialog.date}
                  onChange={e => setCfaDialog(prev => ({ ...prev, date: e.target.value }))}
                  className="input-streamcon" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCfaDialog(null)}>Cancel</Button>
            <Button className="btn-primary gap-1.5" onClick={handleSaveCfaDate}>
              <ShieldCheck className="w-3.5 h-3.5" /> Save CFA Date
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}